import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  MapPin,
  Briefcase,
  Sparkles,
  Search,
  Loader2,
  Trash2,
  Check,
  X,
  User,
  Send,
  FileText,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import Button from '@/components/common/Button'
import { toast } from '@/stores/toastStore'
import { useUpdateProspect, useRemoveProspect } from '@/hooks/useJobProspects'
import type { JobProspect } from '@/types'

const OFFER_STATUS_CONFIG: Record<string, { label: string; classes: string }> = {
  PENDING: { label: 'Pending', classes: 'bg-yellow-100 text-yellow-700' },
  VIEWED: { label: 'Viewed', classes: 'bg-blue-100 text-blue-700' },
  ACCEPTED: { label: 'Accepted', classes: 'bg-green-100 text-green-700' },
  REJECTED: { label: 'Rejected', classes: 'bg-red-100 text-red-700' },
  WITHDRAWN: { label: 'Withdrawn', classes: 'bg-gray-100 text-gray-500' },
  EXPIRED: { label: 'Expired', classes: 'bg-orange-100 text-orange-700' },
}

interface ProspectCardProps {
  prospect: JobProspect
  allOffers?: { id: string; status: string; createdAt: string }[]
  jobId?: string
}

export default function ProspectCard({ prospect, allOffers, jobId }: ProspectCardProps) {
  const [editingNotes, setEditingNotes] = useState(false)
  const [notesValue, setNotesValue] = useState(prospect.notes || '')
  const [showAllOffers, setShowAllOffers] = useState(false)

  const navigate = useNavigate()
  const updateProspect = useUpdateProspect()
  const removeProspect = useRemoveProspect()

  const candidate = prospect.candidate

  const offers =
    allOffers ??
    (prospect.offers && prospect.offers.length > 0
      ? prospect.offers
      : prospect.offer
        ? [prospect.offer]
        : [])
  const hasMultipleOffers = offers.length > 1
  const latestOffer = offers[0]

  const handleSaveNotes = async () => {
    try {
      await updateProspect.mutateAsync({
        id: prospect.id,
        data: { notes: notesValue.trim() || undefined },
      })
      setEditingNotes(false)
      toast.success('Notes saved')
    } catch {
      toast.error('Failed to save notes')
    }
  }

  const handleRemove = async () => {
    try {
      await removeProspect.mutateAsync(prospect.id)
      toast.success('Prospect removed')
    } catch {
      toast.error('Failed to remove prospect')
    }
  }

  const displayName =
    candidate?.firstName || candidate?.lastName
      ? `${candidate.firstName ?? ''} ${candidate.lastName ?? ''}`.trim()
      : 'Candidate'

  const handlePrepareOffer = () => {
    const resolvedJobId = jobId || prospect.recruiterJobId
    const params = new URLSearchParams({
      candidateId: prospect.candidateId,
      candidateName: displayName,
    })
    if (resolvedJobId) params.set('jobId', resolvedJobId)
    navigate(`/recruiter/job-offers/new?${params}`)
  }

  const latestOfferCfg = latestOffer
    ? (OFFER_STATUS_CONFIG[latestOffer.status] ?? {
        label: latestOffer.status,
        classes: 'bg-gray-100 text-gray-700',
      })
    : null

  return (
    <div
      className={`bg-white border p-4 shadow-sm transition-opacity overflow-visible ${prospect.status === 'REMOVED' ? 'opacity-50' : ''}`}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className="flex-shrink-0">
          {candidate?.profilePhotoUrl ? (
            <img
              src={candidate.profilePhotoUrl}
              alt=""
              className="w-10 h-10 rounded-full rounded-avatar object-cover"
            />
          ) : (
            <div className="w-10 h-10 rounded-full rounded-avatar bg-gray-200 flex items-center justify-center">
              <User className="w-5 h-5 text-gray-400" />
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-grow min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h3 className="font-semibold text-gray-900 truncate">{displayName}</h3>

            {/* Source badge */}
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${
                prospect.source === 'AI_MATCH'
                  ? 'bg-purple-100 text-purple-700'
                  : 'bg-blue-100 text-blue-700'
              }`}
            >
              {prospect.source === 'AI_MATCH' ? (
                <Sparkles className="w-3 h-3" />
              ) : (
                <Search className="w-3 h-3" />
              )}
              {prospect.source === 'AI_MATCH' ? 'AI Match' : 'Search'}
            </span>

            {/* Latest offer badge */}
            {latestOffer && latestOfferCfg && (
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium ${latestOfferCfg.classes}`}
              >
                <FileText className="w-3 h-3" />
                Offer: {latestOfferCfg.label}
                {hasMultipleOffers && <span className="ml-0.5 font-bold">({offers.length})</span>}
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-2">
            {candidate?.location && (
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {candidate.location}
              </span>
            )}
            {candidate?.yearsExperience !== undefined && (
              <span className="flex items-center gap-1">
                <Briefcase className="w-3 h-3" />
                {candidate.yearsExperience} yrs
              </span>
            )}
          </div>

          {/* All offers expandable section */}
          {hasMultipleOffers && showAllOffers && (
            <div className="mb-2 border border-gray-100 bg-gray-50 p-2 space-y-1.5">
              {offers.map((offer) => {
                const cfg = OFFER_STATUS_CONFIG[offer.status] ?? {
                  label: offer.status,
                  classes: 'bg-gray-100 text-gray-700',
                }
                return (
                  <div key={offer.id} className="flex items-center gap-2 text-xs">
                    <FileText className="w-3 h-3 text-gray-400" />
                    <span className={`px-1.5 py-0.5 text-xs font-medium ${cfg.classes}`}>
                      {cfg.label}
                    </span>
                    <span className="text-gray-400">
                      {new Date(offer.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                )
              })}
            </div>
          )}

          {/* Show all offers toggle */}
          {hasMultipleOffers && (
            <button
              onClick={() => setShowAllOffers(!showAllOffers)}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium mb-1"
            >
              {showAllOffers ? (
                <>
                  <ChevronUp className="w-3 h-3" />
                  Hide offers
                </>
              ) : (
                <>
                  <ChevronDown className="w-3 h-3" />
                  Show all offers ({offers.length})
                </>
              )}
            </button>
          )}

          {/* Notes */}
          {editingNotes ? (
            <div className="mt-2">
              <textarea
                value={notesValue}
                onChange={(e) => setNotesValue(e.target.value)}
                className="w-full text-sm p-2 border border-gray-300 resize-none"
                rows={2}
                placeholder="Add notes..."
                autoFocus
              />
              <div className="flex gap-2 mt-1">
                <button
                  onClick={handleSaveNotes}
                  disabled={updateProspect.isPending}
                  className="flex items-center gap-1 text-xs text-green-700 hover:text-green-800 font-medium"
                >
                  {updateProspect.isPending ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Check className="w-3 h-3" />
                  )}
                  Save
                </button>
                <button
                  onClick={() => {
                    setEditingNotes(false)
                    setNotesValue(prospect.notes || '')
                  }}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                >
                  <X className="w-3 h-3" /> Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setEditingNotes(true)}
              className="text-xs text-gray-400 hover:text-gray-600 italic mt-1 text-left"
            >
              {prospect.notes ? prospect.notes : '+ Add notes'}
            </button>
          )}
        </div>

        {/* Actions */}
        <div className="flex-shrink-0 flex flex-col gap-2 items-end">
          {prospect.status !== 'REMOVED' && (
            <Button
              variant="primary"
              size="sm"
              onClick={handlePrepareOffer}
              className="flex items-center gap-1 whitespace-nowrap"
            >
              <Send className="w-3 h-3" />
              Prepare Offer
            </Button>
          )}

          {prospect.status !== 'REMOVED' && (
            <Button
              variant="danger"
              size="sm"
              onClick={handleRemove}
              disabled={removeProspect.isPending}
              className="flex items-center gap-1"
            >
              {removeProspect.isPending ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Trash2 className="w-3 h-3" />
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
